import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@portals-platform/ui';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { triggerClasses } from '../lib/control-classes.js';
import { useFieldA11y } from '../lib/field-context.js';
import { useFieldError } from '../lib/use-field-error.js';

export type ComboboxOption = string | { value: string; label: string; description?: string };

export interface ComboboxFieldProps {
  name: string;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Called after the form value changes; `undefined` means the selection was cleared. */
  onValueChange?: (value: string | undefined) => void;
}

function normalise(opt: ComboboxOption) {
  return typeof opt === 'string' ? { value: opt, label: opt, description: undefined } : opt;
}

/**
 * Searchable single-select. Use instead of `SelectField` once the list is long enough that
 * scanning it is slower than typing.
 *
 * Built on `command` + `popover` (Radix) rather than the registry's newer `combobox`, which
 * is Base UI — see packages/ui/REGISTRY-DIVERGENCES.md.
 */
export function ComboboxField({
  name,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches.',
  disabled,
  className,
  onValueChange,
}: ComboboxFieldProps) {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const hasError = !!useFieldError(name);
  const a11y = useFieldA11y(name);
  const items = options.map(normalise);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const selected = items.find((o) => o.value === field.value);

        return (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                {...a11y}
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                disabled={disabled}
                onBlur={field.onBlur}
                className={triggerClasses({ hasError, className })}
              >
                <span className={cn('truncate', !selected && 'text-muted-foreground/60')}>
                  {selected?.label ?? placeholder}
                </span>
                <ChevronsUpDownIcon
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </Button>
            </PopoverTrigger>
            {/* Match the trigger width so the list never looks detached from its control. */}
            <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
              <Command>
                <CommandInput placeholder={searchPlaceholder} />
                <CommandList>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>
                    {items.map((opt) => (
                      <CommandItem
                        key={opt.value}
                        // cmdk filters on `value`, so feed it the label — users search by
                        // what they can see, not by the option's id.
                        value={opt.label}
                        onSelect={() => {
                          const next = opt.value === field.value ? undefined : opt.value;
                          field.onChange(next);
                          onValueChange?.(next);
                          setOpen(false);
                        }}
                      >
                        <CheckIcon
                          className={cn(
                            'size-4',
                            opt.value === field.value ? 'opacity-100' : 'opacity-0',
                          )}
                          aria-hidden="true"
                        />
                        <span className="flex flex-col">
                          <span>{opt.label}</span>
                          {opt.description && (
                            <span className="text-[11.5px] text-muted-foreground">
                              {opt.description}
                            </span>
                          )}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        );
      }}
    />
  );
}
